import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Paperclip, ImagePlus, Send, Loader2, Smile } from 'lucide-react';
import { useLanguage } from '@/context/language-context';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { nanoid } from 'nanoid';

interface MessageInputProps {
  onSendMessage: (message: string) => Promise<void>;
  partnerId: string;
  placeholder?: string;
}

const MessageInput: React.FC<MessageInputProps> = ({ 
  onSendMessage,
  partnerId,
  placeholder
}) => {
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useLanguage();
  const { toast } = useToast();

  const handleSend = async () => {
    if (!message.trim()) return;
    
    setIsLoading(true);
    try {
      await onSendMessage(message);
      setMessage('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSend();
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: t('Error'),
        description: t('File size should be less than 5MB'),
        variant: 'destructive'
      });
      return;
    }
    
    setIsUploading(true);
    try {
      // Check if the bucket exists
      const { data: bucketExists } = await supabase.storage.getBucket('chat-attachments');
      
      // Create the bucket if it doesn't exist
      if (!bucketExists) {
        await supabase.storage.createBucket('chat-attachments', {
          public: false
        });
      }
      
      // Upload the file with a unique name
      const fileExt = file.name.split('.').pop();
      const fileName = `${nanoid()}.${fileExt}`;
      const filePath = `${partnerId}/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('chat-attachments')
        .upload(filePath, file);
      
      if (uploadError) throw uploadError;
      
      // Get the public URL
      const { data: publicUrlData } = supabase.storage
        .from('chat-attachments')
        .getPublicUrl(filePath);
      
      if (!publicUrlData?.publicUrl) throw new Error('Failed to get public URL');
      
      // Determine file type
      const isImage = file.type.startsWith('image/');
      
      // Send the message with attachment
      toast({
        title: t('Success'),
        description: t('File uploaded successfully'),
      });
      
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: t('Error'),
        description: t('Failed to upload file'),
        variant: 'destructive'
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="border-t p-3">
      <form onSubmit={handleFormSubmit} className="flex items-center space-x-2">
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileUpload}
          accept="image/*,.pdf,.doc,.docx"
          aria-label={t('messages.attach_file', 'Attach file')}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-muted-foreground"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Paperclip size={20} />
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-muted-foreground"
          onClick={() => {
            if (fileInputRef.current) {
              fileInputRef.current.accept = "image/*";
              fileInputRef.current.click();
            }
          }}
          disabled={isUploading}
        >
          <ImagePlus size={20} />
        </Button>
        <Input
          type="text"
          placeholder={placeholder || t('Type your message...')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="flex-grow rounded-full bg-secondary/30"
        />
        <Button 
          type="submit"
          variant="ghost" 
          size="icon" 
          disabled={isLoading || isUploading || !message.trim()}
          className="text-primary hover:text-primary/80"
        >
          {isLoading ? (
            <Loader2 className="animate-spin" size={20} />
          ) : (
            <Send size={20} />
          )}
        </Button>
      </form>
    </div>
  );
};

export default MessageInput;
